import { Col, Row } from 'antd';
import { get } from 'lodash';
import { Helmet } from 'react-helmet';
import { withNamespaces } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { handleApiErrors } from '../../api/axiosInstance';
import PromptsAPI from '../../api/prompts';
import Box from '../../components/Box';
import PromptForm, { DEFAULT_GENERATION_CONFIG } from '../../components/Forms/PromptForm';
import PageBreadcrumbs from '../../components/PageBreadcrumbs';
import SpaceSpinner from '../../components/SpaceSpinner';
import Toast from '../../components/Toast';
import PageContainer from '../../containers/PageContainer';
import { INTERNAL_LINKS } from '../../enum';
import withAuthentication from '../../hocs/withAuthentication';
import { formatPageTitle } from '../../utils/common';

const UpdatePromptView = props => {
  const { history, match, t } = props;
  const queryClient = useQueryClient();
  const promptId = get(match, 'params.id');

  const promptQuery = useQuery({
    enabled: !!promptId,
    queryKey: ['fetchPromptDetails', promptId],
    queryFn: () => PromptsAPI.fetchPrompt(promptId),
    onError: error => {
      handleApiErrors(error.response);
      history.push(INTERNAL_LINKS.PROMPTS);
    },
  });

  const updatePromptMutation = useMutation(data => PromptsAPI.updatePrompt(promptId, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['fetchPrompts'], { exact: false });
      queryClient.invalidateQueries(['fetchPromptDetails', promptId], { exact: false });
      Toast({
        type: 'success',
        message: t('updatePromptSuccess'),
      });
      history.push(INTERNAL_LINKS.PROMPT_DETAILS(promptId));
    },
    onError: error => {
      handleApiErrors(error.response, () => {
        Toast({
          type: 'error',
          message: t('updatePromptError'),
        });
      });
    },
  });

  const initialValues = {
    ...promptQuery.data,
    generationConfig: {
      ...DEFAULT_GENERATION_CONFIG,
      ...(get(promptQuery.data, 'generationConfig') || {}),
    },
  };

  return (
    <PageContainer
      title={
        <PageBreadcrumbs
          items={[
            { label: t('prompts'), onClick: () => history.push(INTERNAL_LINKS.PROMPTS) },
            {
              label: get(promptQuery.data, 'name', ' '),
              onClick: () => history.push(INTERNAL_LINKS.PROMPT_DETAILS(promptId)),
            },
            { label: t('edit') },
          ]}
        />
      }
    >
      <Helmet>
        <title>{formatPageTitle('Edit Prompt')}</title>
      </Helmet>

      <Row justify="center">
        <Col flex="832px">
          <Box>
            {promptQuery.isFetching || !promptQuery.data ? (
              <SpaceSpinner />
            ) : (
              <PromptForm
                t={t}
                isUpdate
                loading={updatePromptMutation.isLoading}
                disabled={updatePromptMutation.isLoading}
                initialValues={initialValues}
                onFinish={updatePromptMutation.mutateAsync}
                onCancel={() => history.push(INTERNAL_LINKS.PROMPT_DETAILS(promptId))}
              />
            )}
          </Box>
        </Col>
      </Row>
    </PageContainer>
  );
};

export default withNamespaces()(withAuthentication(UpdatePromptView));
